import { defineStore } from 'pinia';
import { cloneDeep, each, find, map } from 'lodash';
import Vue from 'vue';

import CashFlowLogApi from '@/extensions/shop/pointOfSale/store/api/cashFlowLog';

export const useCashFlowLogStore = defineStore('cashFlowLog', {
    state: () => ({
        loading: true,
        cashFlowLogsPaginated: {
            variables: {
                paginationSizes: [25, 50, 100, 500],
                order: 'created_at',
                direction: 'desc'
            },
            filters: {},
            pageInfo: {
                total: 0,
                perPage: 25,
                currentPage: 1,
                lastPage: 0
            },
            allColumns: [],
            activeColumns: [],
            defaultSort: { field: 'created_at', direction: 'desc' },
            showFilters: false,
            link: {},
            items: []
        }
    }),

    actions: {
        addPaginatedCashFlowLogsToState(data) {
            // Navigation is handled client-side via the row click (see List.vue),
            // so we drop any column-level links the backend may send to avoid
            // generating invalid routes.
            const allColumns = cloneDeep(data.config.allColumns).map(column => {
                delete column.link;
                return column;
            });

            this.cashFlowLogsPaginated.items = data.items;
            this.cashFlowLogsPaginated.pageInfo = cloneDeep(data.pageInfo);
            this.cashFlowLogsPaginated.allColumns = allColumns;
            this.cashFlowLogsPaginated.link = cloneDeep(data.config.link);
            this.cashFlowLogsPaginated.showFilters = data.config.showFilters || false;

            if (data.config.defaultSort) {
                this.cashFlowLogsPaginated.defaultSort = cloneDeep(data.config.defaultSort);
            }
        },

        defineActiveColumns(activeColumnKeys) {
            // Columns that are always displayed regardless of the backend/user
            // selection, so the cash report PDF is always accessible from the list.
            const alwaysVisibleKeys = ['cash_report_id'];

            let activeColumns = [];
            each(this.cashFlowLogsPaginated.allColumns, column => {
                // The comment column is handled separately below so it is always
                // displayed and always rendered as the last (rightmost) column.
                if (column.key === 'comment') return;

                let allowed = find(activeColumnKeys, value => column.key === value);
                if (allowed || alwaysVisibleKeys.includes(column.key)) activeColumns.push(column);
            });

            const commentColumn = find(this.cashFlowLogsPaginated.allColumns, column => column.key === 'comment');
            if (commentColumn) activeColumns.push(commentColumn);

            if (activeColumns.length) {
                this.cashFlowLogsPaginated.activeColumns = activeColumns;
            }
        },

        defineItemsPerPage(perPage) {
            this.cashFlowLogsPaginated.pageInfo.perPage = perPage;
        },

        defineCurrentPage(currentPage) {
            this.cashFlowLogsPaginated.pageInfo.currentPage = currentPage;
        },

        defineFilterValue({ key, value }) {
            if (value.match === '' && value.like === '' && value.from === '' && value.to === '' && value.in.length === 0) {
                Vue.delete(this.cashFlowLogsPaginated.filters, key);
            } else {
                Vue.set(this.cashFlowLogsPaginated.filters, key, value);
            }
        },

        defineFilterVisibility(value) {
            Vue.set(this.cashFlowLogsPaginated, 'showFilters', value);
        },

        defineSorting({ key, value }) {
            Vue.set(this.cashFlowLogsPaginated.variables, 'order', key);
            Vue.set(this.cashFlowLogsPaginated.variables, 'direction', value);
        },

        definePaginationSizes(value) {
            Vue.set(this.cashFlowLogsPaginated.variables, 'paginationSizes', value);
        },

        updateLoading(value) {
            this.loading = value;
        },

        fetchCashFlowLogsPaginated(payload = {}) {
            return CashFlowLogApi.fetchCashFlowLogsPaginated({ ...payload, ...this.cashFlowLogsPaginated }).then(data => {
                this.addPaginatedCashFlowLogsToState(data);

                const activeColumnKeys = map(data.config.activeColumnKeys, 'key');
                this.defineActiveColumns(activeColumnKeys);
            });
        },

        fetchCashFlowLogDates() {
            return CashFlowLogApi.fetchCashFlowLogDates(this.cashFlowLogsPaginated);
        },

        fetchCashFlowLogsForExport(options = {}) {
            return CashFlowLogApi.fetchCashFlowLogsForExport(this.cashFlowLogsPaginated, options);
        },

        resetCashFlowLogsPaginated() {
            // Only clear the fetched rows. Filters/sorting/pagination are kept so they
            // survive navigation (e.g. clicking a row to open the purchase and back).
            this.cashFlowLogsPaginated.items = [];
        }
    }
});
